import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Form, ListGroup, Row } from "react-bootstrap";
import { Title, Modal, TooltipIcon } from "components";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";
import { GetMultiStoreStores, GetViewableStores, MultiStoreTest, UpdateMultiStore } from "redux/slices/MultiStore";
import { showToast } from "redux/slices/Toasts";
import { onlyUnique } from "helpers/Functions";
import { ZCompactSearchBar } from "components/ZCompactSearchBar";
import { ZButton } from "components/ZButton";
import { ZTooltip } from "components/ZTooltip";

export function SimpleMultiStore() {
	const MAX_NUMBER_SELECTED = 20;
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [filteredStores, setFilteredStores] = useState<any[]>([]);
	const [selectedStoreList, setSelectedStoreList] = useState<any[]>([]);
	const [storeFilter, setStoreFilter] = useState<string>('');

	const [multistoreGroup, setMultistoreGroup] = useState<any[]>([]);

	const [testResult, setTestResult] = useState<any[]>([]);
	const [onlyViewing, setOnlyViewing] = useState<boolean>(false);
	const [viewedStore, setViewedStore] = useState<any>(undefined);

	const [showPrompt, setShowPrompt] = useState<boolean>(false);
	const [showDifferentOwnerPrompt, setShowDifferentOwnerPrompt] = useState<boolean>(false);
	const differentOwnersPromptAction = useRef<() => void>();
	const [addSelectedButtonEnabled, setAddSelectedButtonEnabled] = useState<boolean>(false);
	const [addSelectedButtonError, setAddSelectedButtonError] = useState<string>("");
	const [comment, setComment] = useState<string | null>(null);
	const [stores, setStores] = useState<any[]>([]);

	const selectAllRef = useRef<any>(null);

	useEffect(() => {
		dispatch(GetMultiStoreStores()).then((result) => {
			setStores(result.data ?? []);
		});
	}, []);

	useEffect(() => {
		console.log("Main stores updated", multistoreGroup);
	}, [multistoreGroup]);

	useEffect(() => {
		onFilterChange(storeFilter);
	}, [stores]);

	useEffect(() => {
		if (storeFilter == "" || storeFilter == null) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError("Can't add selected without applying a search filter");
		} else if (onlyViewing) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError(`You are currently viewing a multi-store group, you must clear all before you can add stores`);
		} else if (selectedStoreList.length > MAX_NUMBER_SELECTED) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError(`Can't add more than ${MAX_NUMBER_SELECTED} items at a time`);
		} else if (selectedStoreList.length === 0) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError(`Must select at least 1 store to add`);
		} else {
			setAddSelectedButtonEnabled(true);
			setAddSelectedButtonError("");
		}
	}, [storeFilter, selectedStoreList, onlyViewing]);

	useEffect(() => {
		if (selectAllRef.current) {
			selectAllRef.current.indeterminate = selectedStoreList.length > 0 && selectedStoreList.length != filteredStores.filter(s => s.viewableStoreIds.length === 0).length;
		}
	}, [selectedStoreList]);

	function onFilterChange(value: string) {
		setSelectedStoreList([]);
		var ids = value.split(' ').join(',').split(',');
		let ss: any[] = value.trim() === "" ? [] : stores.filter((i: any) => i.name.toLowerCase().includes(value.toLowerCase()) || ids.includes(i.id.toString()));
		setFilteredStores(ss);
		setStoreFilter(value);
	}

	function addtoMainStores(store: any) {
		if (!multistoreGroup.includes(store)) {
			let owners = multistoreGroup.map(i => i.ownerId);
			if (owners.length > 0 && !owners.includes(store.ownerId)) {
				differentOwnersPromptAction.current = () => setMultistoreGroup([...multistoreGroup, store]);
				setShowDifferentOwnerPrompt(true);
			}
			else {
				setMultistoreGroup([...multistoreGroup, store])	
				differentOwnersPromptAction.current = undefined;		
			}
		}
	}

	function addMultipleToMainStores(storeIds: number[]) {
		const newStoreIds = storeIds.filter(storeId => !multistoreGroup.find(s => s.id === storeId));
		const newStores = stores.filter(s => newStoreIds.includes(s.id));
		const newMultiStoreGroup = [...multistoreGroup, ...newStores];

		setMultistoreGroup(newMultiStoreGroup);
	}

	function removeFromMainStores(store: any) {
		if (multistoreGroup.includes(store)) {
			let temp = [...multistoreGroup];
			temp.splice(temp.indexOf(store), 1);
			setMultistoreGroup(temp)
		}
	}

	function testMultiStore(stores: any[]) {
		if (stores.length > 0) {
			dispatch(MultiStoreTest(stores.map(i => i.id as number))).then((result) => {
				setTestResult(result.stores);
			})
		} else {
			setTestResult([]);
		}
	}


	async function saveMultiStore() {
		let errors: any[] = [];

		if (multistoreGroup.length > 0) {
			for (let index = 0; index < multistoreGroup.length; index++) {
				const element = multistoreGroup[index];

				await dispatch(UpdateMultiStore({ storeId: element.id, viewStoreIds: multistoreGroup.map(i => i.id), comment: comment })).then((result) => {
				});
			}
		}

		if (errors.length > 0) {
			dispatch(showToast({ value: errors }));
		} else {
			dispatch(showToast({ value: ["Done"] }));
			dispatch(GetMultiStoreStores()).then((result) => {
				setStores(result.data ?? []);
			}); 
			clearAll();
		}
	}

	function updateSelectedStoreList(storeId: number, add: boolean) {
		console.log(storeId, add);
		if (add) {
			let newList = [...selectedStoreList];
			newList.push(storeId);
			newList = newList.filter(onlyUnique);
			setSelectedStoreList(newList);
		} else {
			const newList = selectedStoreList.filter(s => s != storeId);
			setSelectedStoreList(newList);
		}
	}

	function selectAllStores() {
		setSelectedStoreList(filteredStores.filter(s => s.viewableStoreIds.length === 0).map(s => s.id));
	}

	function deselectAllStores() {
		setSelectedStoreList([]);
	}

	function viewMultiStoreGroup(storeId: number) {
		dispatch(GetViewableStores(storeId)).then((result) => {
			const comments: Set<string> = new Set<string>();
			const viewableStoreIds = result.data.map(s => {
				comments.add(s.comment);
				return s.viewStoreId;
			})
			if (comments.size > 1) {
				console.error("More than 1 unique comment returned for the given store view!");
			}
			let ss: any[] = stores.filter((i: any) => viewableStoreIds.includes(i.id));
			if (ss.length > 0) {
				let commentsArr: string[] = [];
				comments.forEach((x, y, s) => {
					commentsArr.push(x);
				});
				setOnlyViewing(true);
				setComment(commentsArr.length > 0 ? commentsArr[0] : null);
				setViewedStore(stores.find(s => s.id === storeId));
				setMultistoreGroup(ss);
			}
		});
	}

	function clearAll(){
		setOnlyViewing(false); 
		setViewedStore(undefined); 
		setComment(null); 
		setMultistoreGroup([]);
		setTestResult([])
	}

	return (
		<>
			<Title pageTitle="Multi-Store" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col xs={6} className="d-flex flex-column">
									<h3>Search</h3>
									<ZCompactSearchBar placeholderText={"Search by site id separated by space ( ) or comma (,)"} className="ms-1 mb-2" searchText={storeFilter} onChange={(t) => onFilterChange(t)} />
									{filteredStores.filter(s => !multistoreGroup.includes(s)).length > 0 ? 
										<><div style={{width:"fit-content"}} className="d-flex prevent-select simple-multistore-store-btn ms-1 p-1 align-items-center mb-1"
											onClick={(e) => {
												selectAllRef.current.checked ? deselectAllStores() : selectAllStores()
											}}>
											<Form.Check ref={selectAllRef} onClick={(e) => e.stopPropagation() } onChange={(e) => { e.target.checked ? selectAllStores() : deselectAllStores() }} checked={selectedStoreList.length != 0 && selectedStoreList.length === filteredStores.filter(s => s.viewableStoreIds.length === 0).length} className="align-self-center" />
											<div className="d-flex ms-2" style={{textAlign: "left"}}>Select all</div>
										</div>
										<hr className="mt-1 mb-1 border-1 mx-1" /></> : <></> 
									}
									<div className="scrollable-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
										<Form>
											{filteredStores.filter(s => !multistoreGroup.includes(s)).map((store: any, index: any) => (
												<div key={index} className="d-flex prevent-select" style={{width:"100%"}}>
													<div className={`simple-multistore-store-btn pt-1 pb-1 pe-1 d-flex mb-1 align-items-center ${store.viewableStoreIds.length > 0 && "disabled"}`} style={{flexGrow: 1}} onClick={(e) => { store.viewableStoreIds.length === 0 && updateSelectedStoreList(store.id, !selectedStoreList.some(s => s === store.id));  }}>
														<div className="ms-2">
															<Form.Check disabled={store.viewableStoreIds.length > 0} onClick={(e) => e.stopPropagation() } onChange={(e) => { updateSelectedStoreList(store.id, e.target.checked)}} checked={selectedStoreList.some(s => s === store.id)} className="align-self-center" />
														</div>
														<div className="d-flex ms-2" style={{textAlign: "left"}}>{store.id} {store.name}</div>
														{store.viewableStoreIds.length > 0 ? 
															<div className="d-flex ms-auto align-items-center">
																<div className="btn btn-light px-1 py-0"
																	style={{background: "none"}}
																	onClick={(e) => {
																		e.stopPropagation();
																		viewMultiStoreGroup(store.id);
																	}}>
																	<i className="mdi mdi-eye"></i>
																</div>
																<TooltipIcon classes={["px-1 py-0"]} text={"This store is already part of a Multi-Store group"} placement={"right"} icon={"mdi mdi-storefront"}></TooltipIcon>
															</div> : <></> }
													</div>
												</div>
											))}
										</Form>
									</div>
								</Col>

								<Col xs={6} className="pe-0">
									<div className="d-flex align-items-center mb-2">
										<h3 className="mb-0">Multi-Store Group</h3> 
										{onlyViewing && viewedStore ? 
											<TooltipIcon icon="mdi mdi-eye" classes={["h-fit", "fs-5"]} text={`Viewing ${viewedStore.id} ${viewedStore.name}`} placement={"bottom"} />
											: <></>
										}
									</div>
									<div className="d-flex align-items-center pt-1">
										<Button variant="danger" className="py-0 px-1" disabled={multistoreGroup.length === 0} onClick={() => { setOnlyViewing(false); setViewedStore(undefined); setComment(null); setMultistoreGroup([]); }}>
											Clear All
										</Button>
										<Button variant="secondary" className="ms-1 me-1 py-0 px-1" disabled={multistoreGroup.length === 0} onClick={() => testMultiStore(multistoreGroup)}>
											View Test
										</Button>
										<Button variant="primary" className="ms-auto py-0 px-1" disabled={multistoreGroup.length === 0 || onlyViewing || !comment || comment.length === 0} onClick={() => setShowPrompt(true)}>
											<i className="mdi mdi-content-save"></i> Save
										</Button>
									</div>
									<div className="mt-3">
										<Form.Group className="mb-2 d-flex flex-row align-items-center" style={{width:"100%"}}>
											<Form.Label className="me-1">Comment: </Form.Label>
											<Form.Control
												required
												type="text"
												name="comment"
												id="comment"
												placeholder="Enter your name here"
												value={comment ?? ""}
												disabled={onlyViewing}
												onChange={(e) => {
													setComment(e.target.value.length > 0 ? e.target.value : null);
												}}
											/>
										</Form.Group>
									</div>
									<div className="scrollable-container pe-1 me-0" style={{ maxHeight: '520px', overflowY: 'auto'}}>
										<ListGroup>
											{multistoreGroup.map((store: any, index: any) => (
												<div key={index} className="d-flex prevent-select" style={{width:"100%"}}>
												<div key={index} className="d-flex mb-1 p-1 align-items-center" style={{flexGrow: 1}}>
													<div className="d-flex me-1" style={{textAlign: "left"}}>{store.id} {store.name}</div>
													{onlyViewing ? <></> : 
														<Button variant="danger" className="ms-auto px-1 py-0" onClick={() => removeFromMainStores(store)}>
															<i className="mdi mdi-trash-can"></i>
														</Button>
													}
												</div>
											</div>
											))}
										</ListGroup>
									</div>
								</Col>
							</Row>

							{filteredStores.length > 0 ? <Row>
								<Col xs={6} className="d-flex justify-content-end">
									<ZTooltip
										enabled={!addSelectedButtonEnabled} 
										text={addSelectedButtonError} 
										placement="top" 
									>
										<ZButton 
											disabled={!addSelectedButtonEnabled}
											onClick={(e) => {
												addMultipleToMainStores(selectedStoreList);
												setSelectedStoreList([]);
											}}
											buttonText="Add Selected"
										/>
									</ZTooltip>
								</Col>
								<Col xs={6}></Col>
							</Row> : <></>}
						</Card.Body>
					</Card>

					<Card>
						<Card.Body>
							<Row className="mb-1">
								<div style={{ maxHeight: '500px', overflowY: 'auto' }}>

									<div className="d-flex align-items-center">
										<h3 className="m-0">Multi-Store View</h3>
										<Button className="ms-2 py-0 px-1" variant="danger" onClick={() => setTestResult([])}>
											Clear All
										</Button>
									</div>


									<br />

									{testResult.map((item: any, index: number) => (
										<div>
											{item.storeId} - {item.storeName} - {item.errors.length > 0 ? item.errors.join(", ") : item.response && item.response.name + ' Ph:' + item.response.phoneNo}
										</div>
									))}
								</div>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Modal
				title="Confirm - Save Multistore"
				show={showPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowPrompt(false)}
				handleSubmit={() => {
					saveMultiStore();
					setShowPrompt(false);
				}}
				description={
					<>
						<Alert variant="danger">
							<div className="d-flex flex-row">
								<i className="mdi mdi-alert me-1"></i> 
								<div>
									Data breaches can occur with the incorrect selection of stores/store groups.
									<br/><p className="m-0 mt-1 fw-bold">Please double-check your selection.</p>
								</div>
							</div>
						</Alert>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>

			<Modal
				title="Different Owners!"
				show={showDifferentOwnerPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowDifferentOwnerPrompt(false)}
				handleSubmit={() => {
					differentOwnersPromptAction.current && differentOwnersPromptAction.current();
					setShowDifferentOwnerPrompt(false);
				}}
				description={
					<>
						<p>This store does not belong to any of the owners of the existing stores! Are you sure they want to see each other?</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>
		</>
	);
}